import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase.js';

const REFRESH_MS = 5 * 60 * 1000;

const EVENT_COLORS = [
  'var(--accent-blue)',
  'var(--accent-green)',
  'var(--accent-purple)',
  'var(--accent-amber)',
  'var(--accent-red)',
];

function formatTime(iso) {
  if (!iso || !iso.includes('T')) return 'All day';
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function TodayCalendar() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function load() {
      setError(null);
      const { data, error: invokeErr } = await supabase.functions.invoke('get-calendar');
      if (!mounted) return;
      if (invokeErr) {
        setError(invokeErr.message);
      } else if (data?.error) {
        setError(data.error);
      } else {
        setEvents(data?.events ?? []);
      }
      setLoading(false);
    }

    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  return (
    <Card
      title="Today's Schedule"
      badge={events.length > 0 ? `${events.length} event${events.length === 1 ? '' : 's'}` : null}
    >
      {loading ? (
        <Placeholder>Loading…</Placeholder>
      ) : error ? (
        <Placeholder error>
          Calendar error: {error}
          <div className="text-xs text-text-muted mt-1">Check Supabase Edge Function logs</div>
        </Placeholder>
      ) : events.length === 0 ? (
        <Placeholder>No events today. Wide open.</Placeholder>
      ) : (
        <ul className="space-y-2">
          {events.map((evt, i) => (
            <li
              key={evt.id || i}
              className="flex items-stretch gap-3 bg-bg-input/40 rounded p-2 animate-fade-in"
              style={{ animationDelay: `${i * 30}ms` }}
            >
              <span
                className="w-1 rounded-full shrink-0"
                style={{ background: EVENT_COLORS[i % EVENT_COLORS.length] }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-text-primary truncate">{evt.title}</div>
                <div className="text-xs text-text-secondary mt-0.5">
                  {evt.allDay
                    ? 'All day'
                    : `${formatTime(evt.start)} — ${formatTime(evt.end)}`}
                  {evt.location ? ` · ${evt.location}` : ''}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Card({ title, badge, children }) {
  return (
    <div className="bg-bg-card border border-border-base rounded p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs uppercase tracking-wider text-text-secondary">
          📅 {title}
        </h3>
        {badge && (
          <span className="text-[10px] px-2 py-0.5 bg-accent-blue/15 text-accent-blue rounded">
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function Placeholder({ children, error }) {
  return (
    <div className={`text-sm py-4 text-center ${error ? 'text-accent-red' : 'text-text-muted'}`}>
      {children}
    </div>
  );
}
